// Structured Data utilities for Schema.org markup

export interface OrganizationSchema {
  '@context': string;
  '@type': 'Organization';
  name: string;
  url: string;
  logo: string;
  description: string;
  email: string;
  address: {
    '@type': 'PostalAddress';
    addressLocality: string;
    addressRegion: string;
    addressCountry: string;
  };
  sameAs: string[];
}

export interface WebsiteSchema {
  '@context': string;
  '@type': 'WebSite';
  name: string;
  url: string;
  description: string;
  publisher: {
    '@type': 'Organization';
    name: string;
  };
}

export interface ServiceSchema {
  '@context': string;
  '@type': 'Service';
  name: string;
  description: string;
  provider: {
    '@type': 'Organization';
    name: string;
    url: string;
  };
  serviceType: string;
  areaServed: string;
}

export interface ArticleSchema {
  '@context': string;
  '@type': 'Article';
  headline: string;
  description: string;
  author: {
    '@type': 'Organization';
    name: string;
  };
  publisher: {
    '@type': 'Organization';
    name: string;
    logo: {
      '@type': 'ImageObject';
      url: string;
    };
  };
  datePublished: string;
  dateModified: string;
  mainEntityOfPage: {
    '@type': 'WebPage';
    '@id': string;
  };
}

// Helper function to inject structured data into the page
export const injectStructuredData = (data: any) => {
  // Remove existing structured data with the same type
  const existingScript = document.querySelector(`script[type="application/ld+json"][data-schema="${data['@type']}"]`);
  if (existingScript) {
    existingScript.remove();
  }

  // Create new script tag
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema', data['@type']);
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

// Predefined schemas for DocketLabs
export const getDocketLabsOrganizationSchema = (): OrganizationSchema => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'DocketLabs',
  url: 'https://docketlabs.com',
  logo: 'https://docketlabs.com/lovable-uploads/Docket_Labs_logo-blue-black.png',
  description: 'AI legal consulting and strategy for law firms. Expert training, custom solutions, and advisory services.',
  email: 'info@docketlabs.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'San Francisco',
    addressRegion: 'CA',
    addressCountry: 'US'
  },
  sameAs: [
    'https://linkedin.com/company/docketlabs',
    'https://twitter.com/docketlabs'
  ]
});

export const getDocketLabsWebsiteSchema = (): WebsiteSchema => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'DocketLabs',
  url: 'https://docketlabs.com',
  description: 'Transform your legal practice with AI consulting from DocketLabs. Expert strategy, training & custom solutions for law firms.',
  publisher: {
    '@type': 'Organization',
    name: 'DocketLabs'
  }
});

export const getLegalServiceSchema = (serviceName: string, serviceDescription: string, serviceType: string): ServiceSchema => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: serviceName,
  description: serviceDescription,
  provider: {
    '@type': 'Organization',
    name: 'DocketLabs',
    url: 'https://docketlabs.com'
  },
  serviceType: serviceType,
  areaServed: 'United States'
});

export const getArticleSchema = (
  headline: string, 
  description: string, 
  url: string, 
  datePublished: string, 
  dateModified?: string
): ArticleSchema => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline,
  description,
  author: {
    '@type': 'Organization',
    name: 'DocketLabs'
  },
  publisher: {
    '@type': 'Organization',
    name: 'DocketLabs',
    logo: {
      '@type': 'ImageObject',
      url: 'https://docketlabs.com/lovable-uploads/Docket_Labs_logo-blue-black.png'
    }
  },
  datePublished,
  dateModified: dateModified || datePublished,
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': url
  }
});